import type { Article } from '~/types/Learn/Article';

import { usePublicDetailFetch } from '~/composables/unified/usePublicDetailFetch';
import { fetchArticleLearningAsset } from '~/graphql/documents/learn/articles';
import { LearningAssetType } from '~/graphql/generated/graphql';
import { transformArticleAsset } from '~/utils/transformers/articleTransformers';

interface UseArticleFetchDetailInput {
  slug: string;
}

export const useArticleFetchDetail = async (input: Ref<UseArticleFetchDetailInput>) => {
  const { $urqlClient } = useNuxtApp();
  const route = useRoute();

  // Admin "Preview" links carry a token in `?preview=<token>`; forward it as the header the
  // backend preview gate accepts, so incognito/anonymous preview links resolve the unpublished
  // article. Without it the consumer root serves published rows only.
  const previewToken = computed(() =>
    (typeof route.query.preview === 'string' && route.query.preview !== '' ? route.query.preview : null),
  );

  // Article slugs are unique, so the lookup ignores whatever area the URL names and the page
  // redirects any non-canonical URL to the current one: an old area URL keeps working after the
  // article is re-homed or loses its area.
  // `input` is assigned once and never mutated, so the variables don't need to be watched.
  const variables = computed(() => ({
    filters: {
      slugs: [input.value.slug],
      types: [LearningAssetType.Article],
    },
  }));

  // A preview fetch carries the token header and can resolve a draft, so key it separately from
  // the public route's payload for the same slug.
  const isPreview = computed(() => previewToken.value !== null);

  const { data, fetching, error } = await usePublicDetailFetch({
    key: `article-detail-${input.value.slug}${isPreview.value ? '-preview' : ''}`,
    fetch: async () => {
      const result = await $urqlClient.query(fetchArticleLearningAsset, variables.value, {
        // The token travels as a header, so it never reaches the graphcache key: a preview read
        // must bypass the cache or it resolves the published payload cached for the same slug.
        requestPolicy: previewToken.value ? 'network-only' : 'cache-first',
        ...(previewToken.value ? { fetchOptions: { headers: { 'X-Preview-Token': previewToken.value } } } : {}),
      }).toPromise();
      if (result.error) {
        throw result.error;
      }
      return result.data;
    },
    isNotFound: data => (data?.learningAssets?.data ?? []).length === 0,
    notFoundError: () => new Error(`Article not found with slug: ${input.value.slug}`),
  });

  const article = computed((): Article | null => {
    const asset = data.value?.learningAssets?.data?.[0];
    return asset ? transformArticleAsset(asset) : null;
  });

  return { article, fetching, error };
};
